import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import noResultsIllustration from "resources/images/no-results-x.png";
import { SmallCustomIcon, MediumCustomIcon } from "../../layer00";
import { EmptyStateCard } from "../../layer01";
import {
  MondayMorningWrapUpReport,
  MondayMorningWrapUpReportType,
} from "../../../../types";
import { WrapUpReportOption, WrapUpReportSelect } from "./WrapUpReportSelect";

export type ScoreCardProps = {
  reports: MondayMorningWrapUpReport[];
  date: string;
  isLoading?: boolean;
};

const formatDate = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
    });
  } catch {
    return dateStr;
  }
};

const getNextMonday = (dateStr: string) => {
  const date = new Date(dateStr);
  const daysUntilNextMonday = ((8 - date.getDay()) % 7) || 7;
  date.setDate(date.getDate() + daysUntilNextMonday);
  return date.toISOString();
};

const REPORT_TYPE_ORDER: MondayMorningWrapUpReportType[] = [
  "executive",
  "seniorManager",
  "teamLeader",
];

const getReportTypeRank = (report: MondayMorningWrapUpReport) =>
  report.reportType ? REPORT_TYPE_ORDER.indexOf(report.reportType) : -1;

const getReportLabel = (report: MondayMorningWrapUpReport, index: number) => {
  switch (report.reportType) {
    case "seniorManager":
      return "Senior Manager Check in Report";
    case "executive":
      return "Executive Check in Report";
    case "teamLeader":
      return `${report.teamName ?? "Team"} Check in Report`;
    default:
      return `Check in Report ${index + 1}`;
  }
};

export const ScoreCard = ({ reports, date, isLoading }: ScoreCardProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  const sortedReports = useMemo(
    () =>
      reports
        // Older API responses have no id/reportType, so fall back to the report's position
        .map((report, index) => ({
          ...report,
          id: report.id ?? index,
          label: getReportLabel(report, index),
        }))
        .sort(
          (a, b) =>
            getReportTypeRank(a) - getReportTypeRank(b) ||
            a.label.localeCompare(b.label, undefined, { numeric: true })
        ),
    [reports]
  );

  const reportOptions = useMemo<WrapUpReportOption[]>(() => {
    // Team leader reports only get a section header when other report types sit alongside them
    const hasOtherReportTypes = sortedReports.some(
      (report) => report.reportType !== "teamLeader"
    );
    return sortedReports.map((report) => ({
      id: report.id,
      label: report.label,
      group:
        report.reportType === "teamLeader" && hasOtherReportTypes
          ? "Team Leader Reports"
          : null,
    }));
  }, [sortedReports]);

  const selectedReport =
    sortedReports.find((report) => report.id === selectedReportId) ??
    sortedReports[0];
  const currentReport = selectedReport?.rawReport ?? "";
  const hasReportSelect = !isLoading && reportOptions.length > 1;
  const checkInCompletionInfo = selectedReport?.checkInCompletionInfo ?? null;

  const chips: { variant: string; label: string; icon: React.ReactElement }[] =
    [];

  if (checkInCompletionInfo) {
    const { totalUserWhoCompletedCheckIns, totalUserWithCheckInConversations } =
      checkInCompletionInfo;
    chips.push({
      variant: "warning",
      label: `${totalUserWhoCompletedCheckIns}/${totalUserWithCheckInConversations} checked in`,
      icon: <SmallCustomIcon icon="CHECK" stroke="var(--team-app-score-moderate)" />,
    });
  }

  const renderChips = () => {
    if (chips.length === 0) return null;
    return (
      <Stack direction="row" flexWrap="wrap" gap=".5rem">
        {chips.map((chip) => (
          <Chip
            key={chip.label}
            sx={{ paddingRight: 0, paddingLeft: ".5rem", gap: 0 }}
            variant={chip.variant as any}
            label={chip.label}
            icon={chip.icon}
          />
        ))}
      </Stack>
    );
  };

  const getCardBody = () => {
    if (isLoading) {
      return (
        <Box px="1rem" pb="1rem">
          <Skeleton
            variant="rounded"
            height={96}
            sx={{ borderRadius: "1rem" }}
          />
        </Box>
      );
    }
    if (reports.length === 0) {
      return (
        <EmptyStateCard
          image={noResultsIllustration}
          title="No reports generated for this week"
          body="You have no reports this week to display."
        />
      );
    }
    return (
      <Stack direction="column" rowGap="1rem">
        <Box
          className="raw-email-html"
          sx={{ fontSize: ".875rem", color: "text.primary" }}
          dangerouslySetInnerHTML={{ __html: currentReport }}
        />
      </Stack>
    );
  };

  return (
    <Card className="bg-white pb-0">
      <CardHeader
        className={`px-0 ${open ? "border-b border-[#8080801f]" : ""}`}
        disableTypography
        onClick={() => setOpen((prev) => !prev)}
        sx={{ cursor: "pointer" }}
        title={
          // Badges drop below the title only when the report select needs the header space
          <Stack
            direction={hasReportSelect ? "column" : "row"}
            flexWrap="wrap"
            gap=".5rem"
          >
            <Typography variant="cardTitle">Monday morning wrap up</Typography>
            <Stack direction="row" flexWrap="wrap" gap=".5rem">
              {!isLoading && date && (
                <Chip
                  variant="info"
                  label={`Generated on ${formatDate(getNextMonday(date))}, for week of ${formatDate(date)}`}
                  sx={{ paddingLeft: 0, paddingRight: 0 }}
                />
              )}
              {renderChips()}
            </Stack>
          </Stack>
        }
        action={
          <Stack direction="row" alignItems="center" gap=".5rem">
            {hasReportSelect && (
              <WrapUpReportSelect
                options={reportOptions}
                value={selectedReport?.id ?? null}
                onChange={setSelectedReportId}
              />
            )}
            <IconButton sx={{ border: "none" }}>
              <MediumCustomIcon
                icon="CHEVRONUP"
                stroke={theme.palette.secondary.light}
                rotated={!open}
              />
            </IconButton>
          </Stack>
        }
      />
      <Collapse in={open}>
        <CardContent
          sx={{
            background: "none",
            paddingLeft: 0,
            paddingRight: 0,
            paddingBottom: "0 !important",
          }}
        >
          {getCardBody()}
        </CardContent>
      </Collapse>
    </Card>
  );
};
